import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Box, Typography, TextField, Select, MenuItem, Button, Toolbar, InputAdornment } from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FlowsheetModal from './FlowsheetModal';

export default function FlowsheetTable({ rows = [], loading = false, onRefresh, pageSize = 10, onPageSizeChange }) {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editFlowsheet, setEditFlowsheet] = useState(null);
  const [selectedEncounterId, setSelectedEncounterId] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [triggerFilter, setTriggerFilter] = useState('');

  // Unique values for dropdowns
  const assignedToOptions = Array.from(new Set(rows.map(r => r.assignedTo).filter(Boolean)));
  const roomOptions = Array.from(new Set(rows.map(r => r.room).filter(Boolean)));
  const triggerOptions = Array.from(new Set(rows.map(r => r.trigger).filter(Boolean)));

  const filtered = rows.filter(f =>
    ((f.patientName || '').toLowerCase().includes(search.toLowerCase()) ||
      (f.mrn || '').includes(search) ||
      (f.room || '').toLowerCase().includes(search.toLowerCase())) &&
    (!assignedToFilter || f.assignedTo === assignedToFilter) &&
    (!roomFilter || f.room === roomFilter) &&
    (!triggerFilter || f.trigger === triggerFilter)
  );

  const columns = [
    {
      field: 'time',
      headerName: 'Time',
      flex: 1,
      minWidth: 120,
    },
    { field: 'patientName', headerName: 'Patient Name', flex: 1, minWidth: 140 },
    { field: 'mrn', headerName: 'MRN', flex: 0.7, minWidth: 90 },
    { field: 'dob', headerName: 'DOB', flex: 0.8, minWidth: 110 },
    {
      field: 'encounterId',
      headerName: 'Encounter',
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <Link 
          to={`/encounters/${params.value}`}
          style={{
            color: '#1976d2',
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}
          onMouseEnter={(e) => {
            e.target.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.target.style.textDecoration = 'none';
          }}
        >
          View
        </Link>
      ),
      sortable: false,
      filterable: false,
    },
    { field: 'assignedTo', headerName: 'Assigned To', flex: 1, minWidth: 120 },
    { field: 'room', headerName: 'Room #', flex: 0.7, minWidth: 90 },
    { field: 'trigger', headerName: 'Trigger', flex: 0.8, minWidth: 100 },
  ];

  const handleNewFlowsheet = () => {
    setEditFlowsheet(null);
    setModalOpen(true);
    setSelectedEncounterId('');
  };

  const getEncounterIdForNew = () => {
    if (selectedEncounterId) return selectedEncounterId;
    if (filtered.length > 0) return filtered[0].encounterId;
    return '';
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
      <Toolbar sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ flex: 1, minWidth: 200 }}>
          Inpatient Flowsheets
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search by patient, MRN, or room..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Select
          size="small"
          value={assignedToFilter}
          onChange={e => setAssignedToFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Assigned To (All)</MenuItem>
          {assignedToOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </Select>
        <Select
          size="small"
          value={roomFilter}
          onChange={e => setRoomFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">Room (All)</MenuItem>
          {roomOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </Select>
        <Select
          size="small"
          value={triggerFilter}
          onChange={e => setTriggerFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">Trigger (All)</MenuItem>
          {triggerOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </Select>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewFlowsheet}
          sx={{ minWidth: 160, fontWeight: 600 }}
        >
          New Flowsheet
        </Button>
      </Toolbar>
      <Box sx={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={loading}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pagination
          disableSelectionOnClick
          onRowClick={(params) => {
            setEditFlowsheet(params.row);
            setModalOpen(true);
          }}
          getRowId={row => row.id}
          sx={{
            borderRadius: 2,
            fontSize: '0.97rem',
            '& .MuiDataGrid-columnHeaders': { fontWeight: 700 },
            '& .MuiDataGrid-row:hover': { background: '#f5faff' },
            '& .MuiTablePagination-root': {
              fontSize: '1rem',
              minHeight: 56,
              alignItems: 'center',
            },
            '& .MuiTablePagination-toolbar': {
              minHeight: 56,
              alignItems: 'center',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '1rem',
              lineHeight: 1.7,
              alignItems: 'center',
            },
            '& .MuiTablePagination-select': {
              fontSize: '1rem',
              lineHeight: 1.7,
              alignItems: 'center',
            },
            '& .MuiTablePagination-actions': {
              alignItems: 'center',
            },
          }}
        />
      </Box>
      {modalOpen && (
        <FlowsheetModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          flowsheet={editFlowsheet}
          encounterId={editFlowsheet ? editFlowsheet.encounterId : getEncounterIdForNew()}
          onSave={onRefresh}
        />
      )}
    </Paper>
  );
} 